import dayjs from 'dayjs';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getPriceCacheKey, getPriceCatalogKey, isPriceValid } from '../../utils/equipment';
import {
  FetchPricesFulfilledPayload,
  FetchPriceRequestPayload,
  PriceQueue,
  Prices,
  PriceStatus,
  PriceCatalogIDComponent,
  StartLoadingPricesPayload,
  UpdatePriceCatalogPayload,
  EquipmenId,
} from '../../types/equipment';

/**
 * State Definition for Price redux slice
 * - fetchPriceQueue holds price request queue.
 * - priceCatalog holds the price data of equipments,
 */
export interface PricesState {
  fetchPriceQueue: PriceQueue;
  priceCatalog: Prices;
  priceCache: Prices;
  refreshPriceCache?: boolean;
}

const initialState: PricesState = {
  fetchPriceQueue: {},
  priceCatalog: {},
  priceCache: {},
  refreshPriceCache: false,
};

export const pricesSlice = createSlice({
  name: 'prices',
  initialState,
  reducers: {
    /**
     * Add a new Equipment ID to the price fetch request queue.
     * ID's are added to the queue if not already present in the catalog,
     * or has expired, or not valid.
     */
    fetchPriceRequest: (state, action: PayloadAction<FetchPriceRequestPayload>) => {
      const priceCatalogKey = getPriceCatalogKey(action.payload.equipmentId);

      if (!state.priceCatalog[priceCatalogKey]) {
        state.fetchPriceQueue[priceCatalogKey] = {
          equipmentId: action.payload.equipmentId,
          branchId: null,
          companyId: null,
        };
        state.priceCatalog[priceCatalogKey] = {
          equipmentId: action.payload.equipmentId,
          branchId: null,
          companyId: null,
          status: PriceStatus.INIT,
          dateTimeStamp: dayjs().unix(),
        };
      } else {
        const item = state.priceCatalog[priceCatalogKey];
        if (!isPriceValid(item)) {
          state.fetchPriceQueue[priceCatalogKey] = {
            equipmentId: action.payload.equipmentId,
            branchId: null,
            companyId: null,
          };
          state.priceCatalog[priceCatalogKey] = {
            ...state.priceCatalog[priceCatalogKey],
            status: PriceStatus.INIT,
            dateTimeStamp: dayjs().unix(),
            price: undefined,
          };
        }
      }
    },
    /**
     * Clears all IDs from the queue for which price fetch has started.
     */
    startLoadingPrices: (state, action: PayloadAction<StartLoadingPricesPayload>) => {
      action.payload.list?.forEach((e) => {
        const priceCatalogKey = getPriceCatalogKey(e.equipmentId);
        state.priceCatalog[priceCatalogKey] = {
          ...state.priceCatalog[priceCatalogKey],
          status: PriceStatus.LOADING,
          dateTimeStamp: dayjs().unix(),
          price: undefined,
        };
      });
    },
    /**
     * Remove IDs from the queues if which price fetch has failed.
     * Add entry to the price catalog with the error status.
     */
    onLoadingPricesError: (state, action: PayloadAction<{list: PriceCatalogIDComponent[], errorStatus: PriceStatus}>) => {
      action.payload.list?.forEach((e) => {
        const priceCatalogKey = getPriceCatalogKey(e.equipmentId);
        delete state.fetchPriceQueue[priceCatalogKey];
        state.priceCatalog[priceCatalogKey] = {
          ...state.priceCatalog[priceCatalogKey],
          status: action.payload.errorStatus,
          price: undefined,
        };
      });
    },
    /**
     * Add Price data to the price cache.
     */
    fetchPricesFulfilled: (state, action: PayloadAction<FetchPricesFulfilledPayload>) => {
      action.payload.prices.forEach((e) => {
        // Initialise the cache with company price not found if not entry exists in the cache
        if(action.payload.companyId){
          const companyPriceCacheKey = getPriceCacheKey(
            e.equipment_class_id,
            null,
            action.payload.companyId,
          );
          if (companyPriceCacheKey && !state.priceCache[companyPriceCacheKey]) {
            state.priceCache[companyPriceCacheKey] = {
              equipmentId: e.equipment_class_id,
              branchId: null,
              companyId: action.payload.companyId,
              status: PriceStatus.PRICE_NOT_FOUND,
              dateTimeStamp: dayjs().unix(),
            };
          }
        }
        // Insert to the price cache
        const priceCacheKey = getPriceCacheKey(e.equipment_class_id, e.branch_id, e.company_id);
        if(priceCacheKey) {
          state.priceCache[priceCacheKey] = {
            equipmentId: e.equipment_class_id,
            branchId: e.branch_id,
            companyId: e.company_id,
            status: PriceStatus.COMPLETED,
            dateTimeStamp: dayjs().unix(),
            price: e
          }
        }
      });
    },
    /**
     * Populates the price catalog which will be consumed by components like price widget etc.
     */
    updatePriceCatalog: (state, action: PayloadAction<UpdatePriceCatalogPayload>) => {
      const equipmentIds = action.payload.equipmentIds;
      const branchId = action.payload.branchId;
      const companyId = action.payload.companyId;

      const getBestPrice = (equipmentId: EquipmenId) => {
        let bestPrice = null;
        // For a logged in session, checking if the company price exists in the cache and is valid and setting the best price. If the cached price is invalid, the entry is removed
        if (companyId) {
          const companyPriceCacheKey = getPriceCacheKey(equipmentId, null, companyId);
          if (companyPriceCacheKey){
            const companyPrice = state.priceCache[companyPriceCacheKey];
            if(companyPrice && isPriceValid(companyPrice))
            {
              if(companyPrice.status == PriceStatus.COMPLETED) bestPrice = companyPrice;
            }
            else{
              delete state.priceCache[companyPriceCacheKey];
              return null;
            }
          }
        }

        // Checking if the branch price exists in the cache and is valid and setting the best price. If the cached price is invalid, the entry is removed
        if (!bestPrice && branchId) {
          const branchPriceCacheKey = getPriceCacheKey(equipmentId, branchId, null);
          if (branchPriceCacheKey) {
            const branchPrice = state.priceCache[branchPriceCacheKey];
            if(branchPrice && isPriceValid(branchPrice)){
              bestPrice = branchPrice;
            }
            else{
              delete state.priceCache[branchPriceCacheKey];
            }
          }
        }

        return bestPrice;
      };

      equipmentIds.forEach((equipmentId) => {
        const priceCatalogKey = getPriceCatalogKey(equipmentId);
        const bestPrice = getBestPrice(equipmentId);
        if(bestPrice){
          state.priceCatalog[priceCatalogKey] = bestPrice;
          delete state.fetchPriceQueue[priceCatalogKey];
        }
      })

      state.refreshPriceCache = false;
    },
    /**
     * Resets the price catalog.
     */
    resetPriceCatalog: (state) => {
      state.priceCatalog = {}
      state.fetchPriceQueue = {}
    },
    /**
     * Resets the price state.
     */
    refetchPrices: (state) => {
      state.priceCatalog = {}
      state.fetchPriceQueue = {};
      state.priceCache = {};
      state.refreshPriceCache = true;
    },
  },
});

export const {
  fetchPriceRequest,
  startLoadingPrices,
  onLoadingPricesError,
  fetchPricesFulfilled,
  updatePriceCatalog,
  resetPriceCatalog,
  refetchPrices,
} = pricesSlice.actions;

export default pricesSlice.reducer;
